import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import ALL_ROLES from "@/data/roles";
import SearchableRoleDropdown from "@/components/SearchableRoleDropdown";
import SkillsInput from "@/components/SkillsInput";
import { LEVEL_LABELS, deriveLevel } from "@/data/profile";
import { useMarketConfig, price as fmtPrice, MARKET_LABELS } from "@/utils/marketConfig";
import { reviewsFor, heroStackFor } from "@/data/testimonials";
import {
  ArrowRight, Upload, FileText, Check, CheckCircle2, Star, Sparkles, Target,
  Zap, ShieldCheck, TrendingUp, Briefcase, Compass, ScanLine, Wrench, Plus,
} from "lucide-react";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- small reusable bits (module-level = stable identity) ---------- */
function useCountUp(target, { duration = 1400, decimals = 0 } = {}) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let raf, started = false;
    const el = ref.current;
    const run = () => {
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(target * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting && !started) { started = true; run(); } });
    }, { threshold: 0.4 });
    if (el) io.observe(el);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [target, duration]);
  return [decimals ? val.toFixed(decimals) : Math.round(val), ref];
}

const Stat = ({ value, suffix = "", prefix = "", decimals = 0, label }) => {
  const [n, ref] = useCountUp(value, { decimals });
  return (
    <div ref={ref} className="text-center">
      <div className="ap-display text-[34px] md:text-[40px] font-extrabold text-[var(--ap-ink)]">
        {prefix}{Number(n).toLocaleString("en-IN")}{suffix}
      </div>
      <div className="text-[13px] font-medium text-[var(--ap-muted)] mt-1">{label}</div>
    </div>
  );
};

const AvatarStack = ({ people }) => {
  // Real faces overlay the coloured initials; if an image fails to load the
  // initial shows through (graceful fallback, no broken-image icon).
  // Faces follow the visitor's market (heroStackFor; India = legacy set).
  const a = people || heroStackFor("IN");
  return (
    <div className="flex items-center -space-x-2.5">
      {a.map(([t, c, img], i) => (
        <span key={i} className="relative w-9 h-9 rounded-full grid place-items-center text-white text-xs font-bold ring-2 ring-white overflow-hidden"
          style={{ background: c }}>
          {t}
          <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />
        </span>
      ))}
    </div>
  );
};

const FEATURES = [
  { icon: ScanLine, t: "ATS Readiness Analyzer", d: "" /* resolved per-market at render via atsBlurb() */ },
  { icon: Wrench, t: "Blocker Fix Engine", d: "Turns weak lines into recruiter-ready bullets with metrics. One click, copy-paste, done." },
  { icon: TrendingUp, t: "Career Velocity Simulator", d: "See your path: where you are now → target role, salary and timeline, compressed by AI." },
  { icon: Briefcase, t: "Smart Job Matching", d: "Scored against your exact stack and level — only roles you can actually win, not keyword noise." },
  { icon: Compass, t: "Service → Product Roadmap", d: "A 30/60/90 plan to jump from a services role to a product company, with the exact skills to add." },
  { icon: ShieldCheck, t: "Deterministic & Honest", d: "Same resume, same score — every time. No invisible ceiling tuned to make you upgrade." },
];

// Named ATS examples follow the market (Naukri is India-only).
const atsBlurb = (market) =>
  `Scans your resume the way Workday, Greenhouse & ${(!market || market === "IN") ? "Naukri" : "Taleo"} do — scored out of 100 against 180+ role keywords.`;

// Velocity-simulator salary slider per market (monthly, local currency). The
// speed math anchors to each market's midpoint so the curve behaves identically;
// India keeps the exact legacy range/anchor.
const SIM_SALARY = {
  IN: { min: 10000, max: 200000, step: 5000, start: 50000, anchor: 50000, locale: "en-IN" },
  US: { min: 3000, max: 25000, step: 500, start: 8000, anchor: 8000, locale: "en-US" },
  UK: { min: 2000, max: 15000, step: 250, start: 5000, anchor: 5000, locale: "en-GB" },
  CA: { min: 3000, max: 20000, step: 500, start: 7000, anchor: 7000, locale: "en-CA" },
  AU: { min: 3000, max: 20000, step: 500, start: 7500, anchor: 7500, locale: "en-AU" },
};

const STEPS = [
  { n: "01", icon: Upload, t: "Upload your resume", d: "Drop your PDF or DOCX. We parse it exactly the way modern ATS software does." },
  { n: "02", icon: ScanLine, t: "Get your honest diagnosis", d: "A real ATS score plus the exact blockers killing your callbacks — for free." },
  { n: "03", icon: Target, t: "Fix, match & get placed", d: "Apply the fixes to 90+, unlock product-company matches by fit, and follow the roadmap." },
];

// Market-aware FAQ: prices + the "tuned for my market" answer localize from the
// resolved config. India renders byte-identically to the original copy.
const buildFaqs = (mkt, p) => {
  const one = p(mkt.prices.one_time);
  const pass = p(mkt.prices.pass_annual);
  const doc = mkt.doc_term || "resume";
  return [
    { q: "Is the ATS score really free?", a: `Yes. Upload your ${doc} and get a real ATS score plus a full diagnosis of what's holding you back — no card, no signup wall. You only pay ${one} when you want the AI rewrite that takes you to 90+ and the clean download.` },
    mkt.market === "IN"
      ? { q: "Is this built for the Indian job market?", a: "Entirely. Roles, salary bands, company types (service vs product) and 180+ keyword families are tuned for India — Razorpay, Flipkart, PhonePe, TCS, Infosys and beyond." }
      : { q: "Is this tuned for my job market?", a: `Yes. Roles, ${doc} conventions, spelling and keyword families adapt to your region — so the score and rewrite match what recruiters there actually expect.` },
    { q: "How is your score different from other tools?", a: "It's deterministic — the same resume always gets the same score from the same engine. The number is honest feedback, not a figure tuned to push you toward checkout." },
    { q: `What do I get for ${one} vs the Season Pass?`, a: `${one} is a one-time ${doc} analysis + AI rewrite + clean ATS-ready download. The ${pass}/year Season Pass adds the full Career OS — job matching, skill paths and your career companion.` },
  ];
};

/* ====================================================================== */
export default function LandingPage() {
  const navigate = useNavigate();
  const { getHeaders, setUser, user } = useAuth();
  // Localized pricing — market auto-detected from the visitor's IP (geo).
  // RULE: never render a currency until `ready` (a cached/fallback market can be
  // the wrong country — VPN/first visit — and flashes ₹ before flipping to $).
  const { cfg: mkt, ready } = useMarketConfig();
  const p = (amt) => (ready ? fmtPrice(mkt, amt) : "…");

  // velocity simulator — range/currency follow the market (India = legacy exact)
  const sim = SIM_SALARY[mkt.market] || SIM_SALARY.IN;
  const [salary, setSalary] = useState(sim.start);
  const [apps, setApps] = useState(20);
  const salaryRef = useRef(null);
  const appsRef = useRef(null);
  useEffect(() => { setSalary(sim.start); }, [sim.start]); // re-center on market resolve
  const standardMonths = Math.max(1, (6 * (salary / sim.anchor) * (20 / apps))).toFixed(1);
  const placementMonths = Math.max(0.5, (standardMonths / 4)).toFixed(1);
  const speedup = (standardMonths / placementMonths).toFixed(1);

  const updateTrack = useCallback((slider, min, max, val) => {
    if (!slider) return;
    const pct = ((val - min) / (max - min)) * 100;
    slider.style.background = `linear-gradient(to right, #5B3DF5 0%, #5B3DF5 ${pct}%, #E7E7E2 ${pct}%, #E7E7E2 100%)`;
  }, []);
  useEffect(() => { updateTrack(salaryRef.current, sim.min, sim.max, salary); }, [salary, sim.min, sim.max, updateTrack]);
  useEffect(() => { updateTrack(appsRef.current, 1, 100, apps); }, [apps, updateTrack]);

  // intake widget state (full profile + upload → analysis)
  const [widgetStep, setWidgetStep] = useState(1);
  const [targetRole, setTargetRole] = useState("");
  const [years, setYears] = useState("");
  const [levelOverride, setLevelOverride] = useState("");
  const [primarySkills, setPrimarySkills] = useState([]);
  const [currentTitle, setCurrentTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const fileRef = useRef(null);

  const derivedLevel = deriveLevel(years);
  const effectiveLevel = levelOverride || derivedLevel;

  const fetchRoles = async (q) => {
    try {
      const res = await axios.get(`${API}/roles/search`, { params: { q, limit: 20 }, headers: getHeaders() });
      return res.data.results || [];
    } catch { return []; }
  };
  const profilePayload = () => ({
    target_role: targetRole,
    years_experience: years === "" ? null : Number(years),
    level: effectiveLevel || null,
    role_category: "",
    primary_skills: primarySkills,
    current_title: currentTitle,
  });
  const handleRoleSelect = async (role) => {
    setTargetRole(role);
    try {
      await axios.put(`${API}/user/target-role`, { target_role: role }, { headers: getHeaders() });
      if (user) setUser((p) => ({ ...p, target_role: role }));
    } catch { /* anon ok */ }
  };
  const handleContinueToUpload = async () => {
    if (!targetRole) { toast.error("Please select a target role"); return; }
    if (years === "" || isNaN(Number(years)) || Number(years) < 0) { toast.error("Please enter your years of experience"); return; }
    try {
      await axios.put(`${API}/user/profile`, profilePayload(), { headers: getHeaders() });
      if (user) setUser((p) => ({ ...p, target_role: targetRole }));
    } catch { /* anon ok */ }
    setWidgetStep(2);
  };
  const validateAndSet = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    if (!f.name.match(/\.(pdf|docx)$/i)) { toast.error("Only PDF and DOCX accepted"); return; }
    setFile(f);
  };
  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/resume/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded — analysing!");
      navigate("/chat", { state: { resumeId: res.data.id, targetRole, profile: profilePayload() } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  // CTA: focus the intake without the jarring nudge when the card is already
  // on screen (desktop); only scroll when it's off-screen (mobile).
  const startAnalysis = () => {
    const yrs = document.querySelector('[data-testid="widget-years"]');
    const card = document.getElementById("analyze");
    const probe = yrs || card;
    if (!probe) return;
    const r = probe.getBoundingClientRect();
    const visible = r.top >= 56 && r.bottom <= window.innerHeight;
    if (visible) {
      yrs?.focus({ preventScroll: true });
    } else {
      (card || probe).scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => yrs?.focus({ preventScroll: true }), 450);
    }
  };

  // Real brand wordmarks (shared /logos assets; Notion has no wordmark on record,
  // so its icon renders with the name text beside it).
  const companies = [
    { name: "Razorpay", logo: "/logos/da_razorpay.png" },
    { name: "Flipkart", logo: "/logos/da_flipkart.png" },
    { name: "Meta", logo: "/logos/lp_meta.png" },
    { name: "Microsoft", logo: "/logos/lp_microsoft.png" },
    { name: "PhonePe", logo: "/logos/da_phonepe.png" },
    { name: "Zomato", logo: "/logos/lp_zomato.png" },
    { name: "CRED", logo: "/logos/da_cred.png" },
    { name: "Swiggy", logo: "/logos/da_swiggy.png" },
    { name: "Amazon", logo: "/logos/da_amazon.png" },
    { name: "Google", logo: "/logos/da_google.png" },
    { name: "Paytm", logo: "/logos/lp_paytm.png" },
    { name: "Freshworks", logo: "/logos/am_freshworks.png" },
    { name: "Zoho", logo: "/logos/am_zoho.png" },
    { name: "Myntra", logo: "/logos/lp_myntra.png" },
    { name: "Ola", logo: "/logos/lp_ola.png" },
    { name: "Meesho", logo: "/logos/lp_meesho.png" },
    { name: "Groww", logo: "/logos/lp_groww.png" },
    { name: "Zerodha", logo: "/logos/da_zerodha.png" },
    { name: "Postman", logo: "/logos/am_postman.png" },
    { name: "Notion", logo: "/logos/lp_notion.png", showName: true },
  ].filter((c) =>
    // India sees the full wall; other markets only globally recognizable brands
    mkt.market === "IN" ||
    ["Meta", "Microsoft", "Amazon", "Google", "Freshworks", "Postman", "Notion"].includes(c.name)
  );

  return (
    <div className="ap-root" data-testid="landing-page">

      {/* ============ HERO ============ */}
      <section className="ap-mesh ap-grid-overlay relative pt-24 pb-10" data-testid="hero-section" style={{ overflowX: "clip" }}>
        <div className="ap-section grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          {/* copy */}
          <div className="ap-rise">
            <span className="ap-chip ap-rise ap-rise-1" style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)" }}><Sparkles className="w-4 h-4" /> AI Career Intelligence · {MARKET_LABELS[mkt.market] || "India"}</span>
            <h1 className="ap-display font-extrabold mt-5 text-[var(--ap-ink)]" style={{ fontSize: "clamp(34px,5vw,70px)", lineHeight: 1.02, letterSpacing: "-0.03em" }}>
              Stop getting <span className="ap-grad-text">ghosted</span>.<br />Start getting <span className="ap-grad-text">callbacks</span>.
            </h1>
            <p className="text-[17px] md:text-[18px] text-[var(--ap-ink-2)] leading-relaxed mt-5 max-w-[540px]">
              PlacementOS reads your resume the way an ATS does, finds exactly what's holding you back, and builds the path from where you are to placed.
            </p>
            <div className="flex flex-wrap items-center gap-5 mt-7 ap-rise ap-rise-2">
              <AvatarStack people={heroStackFor(mkt.market)} />
              <div>
                <div className="flex items-center gap-1 text-[var(--ap-orange)]">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <div className="text-[13px] text-[var(--ap-muted)] font-medium">Loved by <b className="text-[var(--ap-ink)]">15,247+</b> job-seekers</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-7 ap-rise ap-rise-3">
              <button className="ap-btn ap-btn-pri ap-btn-lg ap-pulse" onClick={startAnalysis} data-testid="hero-cta">
                Analyse my resume — free <ArrowRight className="w-[18px] h-[18px]" />
              </button>
              <button className="ap-btn ap-btn-ghost ap-btn-lg" onClick={() => navigate("/find-jobs")}>Browse jobs</button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-[13px] text-[var(--ap-muted)] font-medium">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[var(--ap-green)]" /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[var(--ap-green)]" /> 4-min analysis</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[var(--ap-green)]" /> 15,247 placed</span>
            </div>
          </div>

          {/* intake card */}
          <div className="relative ap-rise ap-rise-2" id="analyze">
            {/* floating 3D chips */}
            <div className="hidden md:flex ap-glass ap-float absolute -left-7 -top-6 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
              <TrendingUp className="w-5 h-5 text-[var(--ap-green)]" />
              <div><div className="ap-display font-extrabold text-[var(--ap-ink)] leading-none">+23</div><div className="text-[11px] text-[var(--ap-muted)]">score lift</div></div>
            </div>
            <div className="hidden md:flex ap-glass ap-float-2 absolute -right-6 -bottom-7 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
              <span className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "var(--ap-fire)" }}>94</span>
              <div><div className="text-[12px] font-bold text-[var(--ap-ink)] leading-none">94% match</div><div className="text-[11px] text-[var(--ap-muted)]">Business Analyst · {({ US: "Stripe", UK: "Monzo", CA: "Shopify", AU: "Canva" })[mkt.market] || "PhonePe"}</div></div>
            </div>

            <div className="ap-glass ap-tilt rounded-[26px] p-6 md:p-7 relative z-10">
              {/* scan beam accent */}
              <div className="ap-scanbeam" />
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 text-[var(--ap-ink)] font-bold ap-display">
                  <span className="w-7 h-7 rounded-lg grid place-items-center text-white" style={{ background: "var(--ap-fire)" }}><Zap className="w-4 h-4" /></span>
                  Free ATS analysis
                </div>
                <span className="text-[12px] font-semibold text-[var(--ap-muted)]">Step {widgetStep}/2</span>
              </div>

              {widgetStep === 1 ? (
                <div className="space-y-3.5" data-testid="widget-role-selection">
                  <div>
                    <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Years of experience</label>
                    <input type="number" min="0" max="50" value={years}
                      onChange={(e) => { setYears(e.target.value); setLevelOverride(""); }}
                      placeholder="e.g. 4"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white focus:outline-none focus:border-[var(--ap-violet)]"
                      data-testid="widget-years" />
                    {derivedLevel && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="widget-level">
                        {Object.entries(LEVEL_LABELS).map(([k, v]) => (
                          <button type="button" key={k} onClick={() => setLevelOverride(k)}
                            className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold border transition-colors"
                            style={effectiveLevel === k
                              ? { background: "var(--ap-violet)", color: "#fff", borderColor: "var(--ap-violet)" }
                              : { background: "#fff", color: "var(--ap-muted)", borderColor: "var(--ap-line)" }}>
                            {v}{k === derivedLevel ? " · suggested" : ""}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Target role</label>
                    <SearchableRoleDropdown roles={ALL_ROLES} fetchOptions={fetchRoles} allowCustom value={targetRole} onChange={handleRoleSelect}
                      placeholder="Select your target role..." searchPlaceholder="Search roles… e.g. Backend Developer" testId="widget-role-dropdown" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Top skills / stack <span className="text-[var(--ap-muted)] font-normal">(priority order)</span></label>
                    <SkillsInput value={primarySkills} onChange={setPrimarySkills} role={targetRole} testId="widget-skills" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Current / recent title <span className="text-[var(--ap-muted)] font-normal">(optional)</span></label>
                    <input type="text" value={currentTitle} onChange={(e) => setCurrentTitle(e.target.value)} placeholder="e.g. Solution Engineer"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white focus:outline-none focus:border-[var(--ap-violet)]"
                      data-testid="widget-current-title" />
                  </div>
                  <button onClick={handleContinueToUpload} disabled={!targetRole || years === ""}
                    className="ap-btn ap-btn-pri ap-btn-md w-full mt-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                    data-testid="widget-continue-btn">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-center text-[12px] text-[var(--ap-muted)]">Free ATS score · no credit card · 4-minute analysis</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="widget-upload-step">
                  <div className="inline-flex items-center gap-1.5 text-[var(--ap-green)] text-[13px] font-semibold">
                    <Check className="w-4 h-4" /> {targetRole}{effectiveLevel ? ` · ${LEVEL_LABELS[effectiveLevel]}` : ""}
                  </div>
                  <div onClick={() => fileRef.current?.click()} onDrop={(e) => { e.preventDefault(); validateAndSet(e.dataTransfer.files[0]); }} onDragOver={(e) => e.preventDefault()}
                    className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors"
                    style={file ? { borderColor: "var(--ap-green)", background: "rgba(22,163,74,.05)" } : { borderColor: "var(--ap-line)" }}
                    data-testid="widget-dropzone">
                    {file ? (
                      <div className="flex flex-col items-center gap-1.5">
                        <FileText className="w-9 h-9 text-[var(--ap-green)]" />
                        <p className="text-[var(--ap-ink)] font-semibold text-sm">{file.name}</p>
                        <p className="text-[var(--ap-muted)] text-xs">{(file.size / 1024).toFixed(0)} KB</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="w-12 h-12 rounded-2xl grid place-items-center text-white mb-1" style={{ background: "var(--ap-fire)" }}><Upload className="w-6 h-6" /></span>
                        <p className="text-[var(--ap-ink)] text-sm font-medium">Click to upload or drag &amp; drop</p>
                        <p className="text-[var(--ap-muted)] text-xs">PDF or DOCX · up to 5MB</p>
                      </div>
                    )}
                    <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={(e) => validateAndSet(e.target.files[0])} className="hidden" data-testid="widget-file-input" />
                  </div>
                  {file && (
                    <button className="ap-btn ap-btn-pri ap-btn-md w-full" onClick={handleUpload} disabled={uploading} data-testid="widget-upload-btn">
                      {uploading ? "Analysing…" : <>Get my ATS score <ArrowRight className="w-4 h-4" /></>}
                    </button>
                  )}
                  <button className="text-[13px] text-[var(--ap-muted)] hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer mx-auto block" onClick={() => { setWidgetStep(1); setFile(null); }} data-testid="widget-back-btn">← Change profile</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMPANIES MARQUEE ============ */}
      <section className="py-10 border-y border-[var(--ap-line)] bg-white/60" data-testid="trusted-companies-section">
        <p className="text-center text-[12.5px] font-semibold tracking-[0.12em] uppercase text-[var(--ap-muted)] mb-6">Our users landed roles at</p>
        <div className="ap-marquee-wrap overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
          <div className="ap-marquee" aria-hidden="true">
            {[...companies, ...companies].map((c, i) => (
              <span key={i} className="inline-flex items-center gap-2.5 whitespace-nowrap mr-14 align-middle">
                <img src={c.logo} alt={c.name} loading="lazy" className="h-8 w-auto object-contain" style={{ maxWidth: 130 }} />
                {c.showName && <span className="ap-display text-[20px] font-bold text-[var(--ap-ink)]/60">{c.name}</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="ap-section py-10" data-testid="how-section">
        <div className="text-center max-w-[640px] mx-auto">
          <span className="ap-eyebrow">How it works</span>
          <h2 className="ap-h2 mt-3">Three steps from <span className="ap-grad-text">ghosted</span> to hired.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s) => (
            <div key={s.n} className="ap-card ap-pop p-7 relative">
              <span className="ap-display text-[44px] font-extrabold text-[var(--ap-canvas-2)] absolute top-4 right-5 select-none">{s.n}</span>
              <span className="w-12 h-12 rounded-2xl grid place-items-center text-white mb-4" style={{ background: "var(--ap-fire)" }}><s.icon className="w-6 h-6" /></span>
              <h3 className="ap-display font-bold text-[19px] text-[var(--ap-ink)]">{s.t}</h3>
              <p className="text-[14.5px] text-[var(--ap-muted)] leading-relaxed mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FEATURE GRID ============ */}
      <section className="ap-section py-10" data-testid="features-section">
        <div className="text-center max-w-[680px] mx-auto">
          <span className="ap-eyebrow">Everything you need</span>
          <h2 className="ap-h2 mt-3">Not a generic checker. A system that <span className="ap-grad-text">gets you placed</span>.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {FEATURES.map((f) => (
            <div key={f.t} className="ap-card ap-pop p-6">
              <span className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}><f.icon className="w-[22px] h-[22px]" /></span>
              <h3 className="ap-display font-bold text-[17px] text-[var(--ap-ink)]">{f.t}</h3>
              <p className="text-[14px] text-[var(--ap-muted)] leading-relaxed mt-1.5">{f.t === "ATS Readiness Analyzer" ? atsBlurb(mkt.market) : f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ VELOCITY SIMULATOR (interactive feature) ============ */}
      <section className="ap-section py-10" data-testid="velocity-section">
        <div className="ap-card ap-mesh overflow-hidden grid lg:grid-cols-[1fr_1fr] gap-8 p-8 md:p-10 items-center">
          <div>
            <span className="ap-eyebrow">Career Velocity Simulator</span>
            <h2 className="ap-h2 mt-3" style={{ fontSize: "clamp(24px,3vw,34px)" }}>Watch AI compress your path to an offer.</h2>
            <p className="text-[15px] text-[var(--ap-muted)] leading-relaxed mt-3">Adjust your target salary and effort — see how much faster a fixed, matched, roadmap-led search gets you hired.</p>
            <div className="mt-6 space-y-5">
              <div>
                <div className="flex justify-between text-[13px] font-semibold text-[var(--ap-ink)] mb-2"><span>Target monthly salary</span><span className="text-[var(--ap-violet)]">{ready ? mkt.symbol : ""}{salary.toLocaleString(sim.locale)}</span></div>
                <input ref={salaryRef} type="range" min={sim.min} max={sim.max} step={sim.step} value={salary} onChange={(e) => setSalary(Number(e.target.value))} className="ap-range w-full" data-testid="salary-slider" />
              </div>
              <div>
                <div className="flex justify-between text-[13px] font-semibold text-[var(--ap-ink)] mb-2"><span>Applications / week</span><span className="text-[var(--ap-violet)]">{apps}</span></div>
                <input ref={appsRef} type="range" min="1" max="100" step="1" value={apps} onChange={(e) => setApps(Number(e.target.value))} className="ap-range w-full" data-testid="apps-slider" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="ap-card p-6 text-center">
              <div className="text-[12px] font-semibold text-[var(--ap-muted)] uppercase tracking-wide">Standard</div>
              <div className="ap-display text-[44px] font-extrabold text-[var(--ap-ink)] mt-1" data-testid="standard-months">{standardMonths}</div>
              <div className="text-[13px] text-[var(--ap-muted)]">months to hired</div>
            </div>
            <div className="ap-card p-6 text-center relative overflow-hidden" style={{ borderColor: "var(--ap-violet)" }}>
              <div className="absolute inset-0 opacity-[0.06]" style={{ background: "var(--ap-fire)" }} />
              <div className="text-[12px] font-semibold text-[var(--ap-violet)] uppercase tracking-wide relative">With PlacementOS</div>
              <div className="ap-display text-[44px] font-extrabold ap-grad-text mt-1 relative" data-testid="placement-months">{placementMonths}</div>
              <div className="text-[13px] font-semibold text-[var(--ap-ink)] relative" data-testid="speedup-text">{speedup}× faster</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STATS BAND ============ */}
      <section className="ap-section py-10" data-testid="stats-section">
        <div className="ap-card grid grid-cols-2 md:grid-cols-4 gap-6 py-9 px-6">
          <Stat value={15247} suffix="+" label="Resumes analysed" />
          <Stat value={3.1} decimals={1} suffix="×" label="More callbacks" />
          <Stat value={91} suffix="%" label="ATS pass rate after fix" />
          {/* The placed-salary figure is an INDIA stat; other markets get the
              (already-claimed) 4-minute analysis stat instead of a ₹ number. */}
          {mkt.market === "IN"
            ? <Stat value={11.4} decimals={1} prefix="₹" suffix="L" label="Avg. placed salary" />
            : <Stat value={4} suffix=" min" label="Average analysis time" />}
        </div>
      </section>

      {/* ============ REVIEWS WALL ============ */}
      <section className="ap-section py-10" data-testid="reviews-section">
        <div className="text-center max-w-[640px] mx-auto">
          <span className="ap-eyebrow">9,200+ placed</span>
          <h2 className="ap-h2 mt-3">They stopped mass-applying<br />and <span className="ap-grad-text">got placed</span>.</h2>
        </div>
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5 mt-12 [column-fill:_balance]">
          {reviewsFor(mkt.market).map((r, i) => (
            <div key={i} className="ap-card ap-pop p-6 mb-5 break-inside-avoid" data-testid={`review-card-${i}`}>
              <div className="flex items-center gap-1 text-[var(--ap-orange)] mb-3">{[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}</div>
              <p className="text-[15px] text-[var(--ap-ink-2)] leading-relaxed">"{r.text}"</p>
              <div className="flex items-center gap-3 mt-4">
                <span className="relative w-10 h-10 rounded-full grid place-items-center text-white text-xs font-bold overflow-hidden shrink-0" style={{ background: r.color }}>
                  {r.avatar}
                  {r.img && <img src={r.img} alt={r.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />}
                </span>
                <div><div className="text-[14px] font-bold text-[var(--ap-ink)]">{r.name}</div><div className="text-[12.5px] text-[var(--ap-muted)]">{r.loc}</div></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="ap-section py-10" data-testid="pricing-section">
        <div className="text-center max-w-[640px] mx-auto">
          <span className="ap-eyebrow">Pricing</span>
          <h2 className="ap-h2 mt-3">Less than a coffee. More than a coach.</h2>
          <p className="text-[15px] text-[var(--ap-muted)] mt-3">Diagnosis is always free. Pay only when you want the fixes.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12 max-w-[860px] mx-auto">
          <div className="ap-card ap-pop p-8" data-testid="pricing-card-roast">
            <span className="ap-chip">Resume Analysis</span>
            <div className="flex items-end gap-2 mt-5">
              <span className="ap-display text-[44px] font-extrabold text-[var(--ap-ink)]">{p(mkt.prices.one_time)}</span>
              <span className="text-[var(--ap-muted)] line-through mb-2">{p(mkt.prices.one_time_was)}</span>
            </div>
            <p className="text-[14px] text-[var(--ap-muted)] mt-1">Your first step to getting noticed.</p>
            <button className="ap-btn ap-btn-ghost ap-btn-md w-full mt-5" onClick={() => navigate("/intake")} data-testid="pricing-roast-btn">Get Resume Analysis <ArrowRight className="w-4 h-4" /></button>
            <ul className="mt-6 space-y-2.5">
              {["ATS compatibility score + full diagnosis", "Keyword gap report for your target role", "AI rewrite to 90+", "Clean, ATS-ready download (PDF/DOCX)"].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[14px] text-[var(--ap-ink-2)]"><CheckCircle2 className="w-[18px] h-[18px] text-[var(--ap-green)] shrink-0 mt-0.5" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="ap-card ap-pop p-8 relative overflow-hidden" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }} data-testid="pricing-card-full">
            <span className="absolute top-5 right-5 text-[11px] font-bold text-white px-3 py-1 rounded-full" style={{ background: "var(--ap-fire)" }}>MOST POPULAR</span>
            <span className="ap-chip">Full Career OS</span>
            <div className="flex items-end gap-2 mt-5">
              <span className="ap-display text-[44px] font-extrabold ap-grad-text">{p(mkt.prices.pass_annual)}</span>
              <span className="text-[var(--ap-muted)] line-through mb-2">{p(mkt.prices.pass_annual_was)}</span>
            </div>
            <p className="text-[14px] text-[var(--ap-muted)] mt-1">The complete system, resume to offer.</p>
            <button className="ap-btn ap-btn-pri ap-btn-md w-full mt-5" onClick={() => navigate("/season-pass")} data-testid="pricing-full-btn">Start my Career OS <ArrowRight className="w-4 h-4" /></button>
            <ul className="mt-6 space-y-2.5">
              {["Everything in Resume Analysis", "AI career roadmap personalised to you", "Skill-gap analysis + curated courses", "Smart job-matching engine", "Your AI career companion, all year"].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[14px] text-[var(--ap-ink-2)]"><CheckCircle2 className="w-[18px] h-[18px] text-[var(--ap-green)] shrink-0 mt-0.5" />{f}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-center text-[13px] text-[var(--ap-muted)] mt-8">All prices include taxes · Join 15,247 professionals already using PlacementOS</p>
      </section>

      {/* ============ FAQ ============ */}
      <section className="ap-section py-10 max-w-[820px]" data-testid="faq-section">
        <div className="text-center"><span className="ap-eyebrow">Questions</span><h2 className="ap-h2 mt-3">Frequently asked.</h2></div>
        <div className="mt-12 space-y-3.5">
          {buildFaqs(mkt, p).map((f, i) => {
            const open = faqOpen === i;
            return (
              <div key={i} className="ap-card overflow-hidden transition-shadow"
                style={open ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}>
                <button onClick={() => setFaqOpen(open ? -1 : i)} className="group w-full flex items-center justify-between gap-4 text-left px-6 py-5 bg-transparent border-0 cursor-pointer">
                  <span className="ap-display font-bold text-[16.5px] text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">{f.q}</span>
                  <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                    style={{ background: open ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: open ? "#fff" : "var(--ap-violet)" }}>
                    <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                  </span>
                </button>
                <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden"><p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed">{f.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-[14px] text-[var(--ap-muted)] mt-10">Still have a question? <button onClick={() => navigate("/contact")} className="text-[var(--ap-violet)] font-semibold bg-transparent border-0 cursor-pointer hover:underline">Talk to us →</button></p>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="ap-section pb-24">
        <div className="text-center overflow-hidden py-20 px-6 rounded-[28px]" style={{ background: "var(--ap-fire)", boxShadow: "0 30px 70px -28px rgba(107,92,230,.5)" }}>
          <h2 className="ap-display font-extrabold text-white" style={{ fontSize: "clamp(30px,4.4vw,50px)", lineHeight: 1.04, letterSpacing: "-0.02em" }}>Your next role is already posted.</h2>
          <p className="text-white/85 mt-3.5 text-[16px] md:text-[17px]">Find out why it hasn't replied yet — in four minutes, free.</p>
          <button className="ap-btn ap-btn-lg mt-7 bg-white shadow-[0_12px_30px_-10px_rgba(0,0,0,.35)] hover:-translate-y-0.5" style={{ color: "var(--ap-violet)" }} onClick={startAnalysis}>
            Start my free analysis <ArrowRight className="w-[18px] h-[18px]" />
          </button>
        </div>
      </section>
    </div>
  );
}
